import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import DurationInput, { getDurationInputValue } from "../../../../../components/Inputs/DurationInput";
import { parseFloat2, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { buildMissPunchPairUpdate, findContinuousMissPunchSibling, getUnresolvedMissPunchSide, isAddedTimeSheet } from "../helpers";
import { getHourTypeColor } from "../../../../../utils/helpers/hourType";
import { useAccess } from "../../../../../utils/hooks/access";
import NumberInput from "../../../../../components/Inputs/NumberInput";
import { usePayCodesMap } from "../../../../Settings/PayPolicies/CalculationGroup/Form/hooks/usePayCodeMap";
import { getMandatoryBreak, isMandatoryBreakViolated } from "../../MandatoryBreakWarning/helpers";

export const getTimeSheetDuration = (timeSheet) => {
    const { startDate, endDate, payCode } = timeSheet;
    if (!startDate || !endDate) {
        return 0;
    }
    if (payCode?.hourType === "unit") {
        return timeSheet.duration;
    }
    return moment(endDate).diff(moment(startDate), "second");
}

export const useDurationColumn = ({ canEdit, openMandatoryBreakWarning }) => {
    const { t } = useTranslation();
    const durationInputRef = useRef();
    const numberInputRef = useRef();
    const { hasAccess: canUpdateDuration } = useAccess("timeSheet.canUpdateDuration");

    const map = usePayCodesMap();

    return useMemo(() => ({
        Header: t("duration"),
        accessor: "duration",
        alwaysVisible: true,
        Cell: ({ row, cell, updateAddedTimeSheet, updateTimeSheet, data }) => {
            const { original, isNonProductive } = row;
            const { startDate, endDate, locked } = original;
            const payCode = map?.[original?.payCode?.code] || original?.payCode; // for cases when pay code is not returned from the api
            if (!startDate) {
                return null;
            }

            const disabled = (!isAddedTimeSheet(original) && locked)
                || row.disabled || cell.disabled;

            if (isNonProductive) {
                return "-";
            }

            // On a row whose start is the unknown side, a duration has to be measured back from the
            // end that *is* known - deriving an end from the placeholder start would just move the
            // one time on the row that was never in doubt.
            const missingSide = getUnresolvedMissPunchSide(original);

            /**
             * Applies a duration, and carries the moment it pins down to the other half of a
             * continuous miss-punch pair: the row before shares this row's start, the row after
             * shares its end.
             */
            const applyDuration = (updatedFields, pinned) => {
                if (isAddedTimeSheet(original)) {
                    updateAddedTimeSheet(original, updatedFields);
                } else {
                    updateTimeSheet(original.id, updatedFields);
                }
                const pair = pinned ? findContinuousMissPunchSibling(original, data) : undefined;
                const pairFields = buildMissPunchPairUpdate(pair, pinned);
                if (!pairFields) {
                    return;
                }
                const callback = isAddedTimeSheet(pair)
                    ? updateAddedTimeSheet.bind(null, pair)
                    : updateTimeSheet.bind(null, pair.id);
                callback(pairFields);
            };

            if (payCode?.hourType === "unit" || (canUpdateDuration && payCode?.hourType !== "amount")) {
                if (payCode?.inputDurationAsNumber) {
                    return (
                        <NumberInput
                            ref={numberInputRef}
                            value={parseFloat2(moment.duration(original.duration, "second").asHours() || 0)}
                            disabled={disabled || !canEdit}
                            className="time-input"
                            style={{
                                color: '#595959',
                                backgroundColor: 'transparent',
                                border: '1px solid #d9d9d9',
                                fontSize: '0.9rem',
                            }}
                            onChange={(e) => {
                                let val = Number(String(e.target.value).replace(/,/g, ""));
                                if (isNaN(val)) {
                                    return;
                                }
                                const currentValue = parseFloat2(moment.duration(original.duration, "second").asHours() || 0);
                                if (val === currentValue) {
                                    return;
                                }
                                if (val < 0) {
                                    val = 0;
                                }
                                let duration = (startDate && endDate)
                                    ? moment.parseZone(endDate).diff(moment.parseZone(startDate), "seconds")
                                    : 0;
                                if (val) {
                                    duration = moment.duration(val, "hour").asSeconds();
                                }
                                const updatedFields = { duration };
                                let pinned;
                                if (missingSide === "start" && endDate && duration > 0) {
                                    pinned = moment.parseZone(endDate).subtract(duration, "seconds").toISOString(true);
                                    updatedFields.startDate = pinned;
                                } else {
                                    pinned = duration > 0 && startDate
                                        ? moment.parseZone(startDate).add(duration, "seconds").toISOString(true)
                                        : endDate;
                                    updatedFields.endDate = pinned;
                                }

                                const changeValue = (data) => {
                                    if (isAddedTimeSheet(original)) {
                                        updateAddedTimeSheet(original, data);
                                    } else {
                                        updateTimeSheet(original.id, data);
                                    }
                                }

                                const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                                if (mandatoryBreak) {
                                    const inViolation = isMandatoryBreakViolated(
                                        {
                                            ...original,
                                            ...updatedFields,
                                            payCode: original.payCode || updatedFields.payCode,
                                        },
                                        mandatoryBreak,
                                    );
                                    if (inViolation) {
                                        openMandatoryBreakWarning({
                                            column: "duration",
                                            requiredTime: mandatoryBreak.duration,
                                            original,
                                            updatedFields,
                                            onConfirm: () => {
                                                applyDuration(updatedFields, pinned);
                                            },
                                            onCancel: () => {
                                                // WORKAROUND FOR NUMBER INPUT
                                                numberInputRef.current?.reset();
                                            },
                                        });
                                    } else {
                                        applyDuration(updatedFields, pinned);
                                    }
                                } else {
                                    applyDuration(updatedFields, pinned);
                                }
                            }}
                        />
                    );
                }
                const value = renderDurationAsFormat(original.duration, "HH:mm")
                return (
                    <DurationInput
                        ref={durationInputRef}
                        value={value}
                        disabled={disabled || !canEdit}
                        timeFormat="HH:mm"
                        className="time-input"
                        style={{
                            color: '#595959',
                            backgroundColor: 'transparent',
                            border: '1px solid #d9d9d9',
                            fontSize: '0.9rem',
                        }}
                        onChange={(val) => {
                            let duration = (startDate && endDate)
                                ? moment.parseZone(endDate).diff(moment.parseZone(startDate), "seconds")
                                : 0;
                            if (val) {
                                duration = getDurationInputValue(val);
                            }                                const updatedFields = { duration };
                            let pinned;
                            if (missingSide === "start" && endDate && duration > 0) {
                                pinned = moment.parseZone(endDate).subtract(duration, "seconds").toISOString(true);
                                updatedFields.startDate = pinned;
                            } else {
                                pinned = duration > 0 && startDate
                                    ? moment.parseZone(startDate).add(duration, "seconds").toISOString(true)
                                    : endDate;
                                updatedFields.endDate = pinned;
                            }

                            const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                            if (mandatoryBreak) {
                                const inViolation = isMandatoryBreakViolated(
                                    {
                                        ...original,
                                        ...updatedFields,
                                        payCode: original.payCode || updatedFields.payCode,
                                    },
                                    mandatoryBreak,
                                );
                                if (inViolation) {
                                    openMandatoryBreakWarning({
                                        column: "duration",
                                        requiredTime: mandatoryBreak.duration,
                                        original,
                                        updatedFields,
                                        onConfirm: () => applyDuration(updatedFields, pinned),
                                        onCancel: () => durationInputRef.current?.reset(),
                                    });
                                } else {
                                    applyDuration(updatedFields, pinned);
                                }
                            } else {
                                applyDuration(updatedFields, pinned);
                            }
                        }}
                    />
                );
            } else if (payCode?.hourType === "amount") {
                return "-"
            } else {
                const duration = (endDate && startDate) ? moment(endDate).diff(moment(startDate), "second") : 0;
                return renderDurationAsFormat(duration, "HH:mm");
            }
        },
    }), [t, map, canUpdateDuration, canEdit, openMandatoryBreakWarning]);
};
